// src/pages/LoginPage.jsx
/* globals PasswordCredential */

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AUTH_ENDPOINTS, API_URL } from "../config";

import { useMobile } from "../styles/commonHooks";
import { getThemeVars, globalAnimationStyles } from "../styles/theme";

// --- Static feed for the live-terminal column (anonymized, English-only by design) ---
const FEED_SOURCE = [
  ["NEW", "matching",    "looking for: a clinical advisor, child psych, US-based"],
  ["",    "matched",     "Chen ↔ Halpern · investor / pre-seed AI infra"],
  ["NEW", "matching",    "looking for: a co-founder, technical, ML systems"],
  ["",    "intro sent",  "Park → Iyer · GTM lead, B2B SaaS"],
  ["",    "matched",     "Dubois ↔ Vance · designer, fintech onboarding"],
  ["NEW", "matching",    "looking for: an angel who's shipped a marketplace"],
  ["",    "intro sent",  "Mori → Becker · running buddy, sub-3 marathon"],
  ["",    "matched",     "Aoki ↔ Patel · senior backend engineer, Berlin"],
  ["NEW", "matching",    "looking for: an editor for a non-fiction proposal"],
  ["",    "intro sent",  "Ng → Velasquez · Series A growth marketer"],
  ["",    "matched",     "Pereira ↔ Stein · AI/policy partner, Brussels"],
  ["NEW", "matching",    "looking for: a CTO who's done health-tech at scale"],
  ["",    "matched",     "Kimura ↔ Osei · biotech advisor, Series B"],
  ["NEW", "matching",    "looking for: a fractional CFO, SaaS, post-revenue"],
  ["",    "intro sent",  "Reyes → Müller · enterprise sales lead, DACH"],
  ["",    "matched",     "Walsh ↔ Nair · UX researcher, consumer fintech"],
  ["NEW", "matching",    "looking for: a podcast co-host, future of work"],
  ["",    "intro sent",  "Tanaka → Okonkwo · ML infra engineer, remote"],
  ["",    "matched",     "Sharma ↔ Lindqvist · climate-tech operator"],
  ["NEW", "matching",    "looking for: an LP, family office, deep-tech focus"],
  ["",    "intro sent",  "Rossi → Park · product lead, marketplace dynamics"],
  ["",    "matched",     "Ibarra ↔ Cohen · growth marketer, B2C mobile"],
  ["NEW", "matching",    "looking for: a co-founder, design, zero-to-one"],
  ["",    "intro sent",  "Liu → Andersen · board advisor, Nordic expansion"],
  ["",    "matched",     "Diallo ↔ Torres · angel, pre-seed consumer"],
  ["NEW", "matching",    "looking for: a technical writer, API docs, dev tools"],
  ["",    "intro sent",  "Nakamura → Silva · chief of staff, hypergrowth"],
];

// Fisher-Yates shuffle — runs once at module load, stays stable across re-renders
function shuffleOnce(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
const FEED = shuffleOnce(FEED_SOURCE);
const MONO_STACK = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Code", monospace';

export default function LoginPage({
  lang,
  theme,
  translations,
  onLogin,
  isSamsungBrowser = false,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showResetPanel, setShowResetPanel] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetStep, setResetStep] = useState("send");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");

  const getApiErrorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const first = detail[0];
      if (typeof first === "string") return first;
      if (first?.msg) return String(first.msg);
      return fallback;
    }
    if (detail && typeof detail === "object") {
      if (detail.msg) return String(detail.msg);
      if (detail.message) return String(detail.message);
    }
    return fallback;
  };

  const formatResetCode = (digits) => {
    const cleaned = (digits || "").replace(/[^\d]/g, "").slice(0, 6);
    if (cleaned.length <= 3) return cleaned;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  };

  const validateEmailFormat = (value) => {
    const normalized = value.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(normalized);
  };

  const checkResetEmail = async () => {
    const normalized = resetEmail.trim().toLowerCase();
    if (!normalized) {
      setResetError(translations[lang]?.emailRequired || "Email is required");
      return false;
    }
    if (!validateEmailFormat(normalized)) {
      setResetError(lang === "fr" ? "Format d'email invalide" : "Invalid email format");
      return false;
    }
    try {
      const res = await axios.post(`${API_URL}/api/password/reset/check-email`, {
        email: normalized,
      });
      if (!res.data?.exists) {
        setResetError(lang === "fr" ? "Utilisateur avec cet email introuvable" : "User with this email was not found");
        return false;
      }
      if (!res.data?.can_reset_password) {
        setResetError(
          lang === "fr"
            ? "Réinitialisation impossible pour ce compte"
            : "Password reset is unavailable for this account"
        );
        return false;
      }
      return true;
    } catch (err) {
      if (err.response?.status === 404) {
        return true;
      }
      setResetError(getApiErrorMessage(err, translations[lang]?.error || "Error"));
      return false;
    }
  };

  const isMobile = useMobile();
  const themeVars = getThemeVars(theme);
  const accentGradient = themeVars.btnBg;
  const isLightTheme = theme === "light";

  // ── theme tokens (matched with RegisterPage) ─────────────────────────────
  const BG        = isLightTheme ? "#f0f1f5" : "#0d0f18";
  const BORDER    = isLightTheme ? "rgba(99,102,241,0.14)"  : "rgba(255,255,255,0.06)";
  const INPUT_BG  = isLightTheme ? "rgba(255,255,255,0.6)"  : "rgba(255,255,255,0.06)";
  const INPUT_FOCUS_BG = isLightTheme ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.1)";
  const INPUT_SHADOW   = `0 0 0 2px ${themeVars.accent}40`;
  const TEXT      = isLightTheme ? "#111827" : "#e6e8ee";
  const MUTED     = isLightTheme ? "rgba(17,24,39,0.5)"  : "rgba(230,232,238,0.5)";

  // ── Theme-adaptive tokens for the LEFT terminal column ───────────────────
  const term = useMemo(() => {
    if (isLightTheme) {
      return {
        pageBg:               BG,
        leftPanelBg:          BG,
        gridLine:             "rgba(99,102,241,0.07)",
        terminalBg:           "rgba(255,255,255,0.72)",
        terminalBorder:       "1px solid rgba(99,102,241,0.18)",
        terminalShadow:       "0 6px 28px rgba(99,102,241,0.10)",
        terminalHeaderBorder: "1px solid rgba(99,102,241,0.12)",
        rowBorder:            "1px dashed rgba(99,102,241,0.14)",
        eyebrow:              "#6366f1",
        headerText:           "#1f2937",
        bodyText:             "rgba(31,41,55,0.65)",
        rowText:              "rgba(31,41,55,0.85)",
        muted:                "rgba(31,41,55,0.55)",
        promptText:           "rgba(31,41,55,0.7)",
        prompt:               "#16a34a",
        cursor:               "#7c3aed",
        tagNew:               "#16a34a",
        tagMatched:           "#0891b2",
        tagIntro:             "#7c3aed",
        tagMatching:          "#d97706",
        gradFrom:             "#6366f1",
        gradTo:               "#06b6d4",
        accentLine:           "linear-gradient(to bottom, transparent, #6366f1, #06b6d4, transparent)",
        divider:              "1px solid rgba(99,102,241,0.14)",
      };
    }
    return {
      pageBg:               BG,
      leftPanelBg:          BG,
      gridLine:             "rgba(255,255,255,0.04)",
      terminalBg:           "rgba(0,0,0,0.4)",
      terminalBorder:       "1px solid rgba(255,255,255,0.08)",
      terminalShadow:       "none",
      terminalHeaderBorder: "1px solid rgba(255,255,255,0.06)",
      rowBorder:            "1px dashed rgba(255,255,255,0.05)",
      eyebrow:              "#a78bfa",
      headerText:           "#e6e8ee",
      bodyText:             "rgba(230,232,238,0.6)",
      rowText:              "rgba(230,232,238,0.78)",
      muted:                "rgba(230,232,238,0.45)",
      promptText:           "rgba(230,232,238,0.7)",
      prompt:               "#4ade80",
      cursor:               "#a78bfa",
      tagNew:               "#4ade80",
      tagMatched:           "#22d3ee",
      tagIntro:             "#a78bfa",
      tagMatching:          "#f59e0b",
      gradFrom:             "#a78bfa",
      gradTo:               "#22d3ee",
      accentLine:           "linear-gradient(to bottom, transparent, #a78bfa, #22d3ee, transparent)",
      divider:              "1px solid rgba(255,255,255,0.06)",
    };
  }, [isLightTheme, BG]);

  const heroEyebrow = lang === "fr" ? "// FLUX EN DIRECT · ANONYMISÉ" : "// LIVE FEED · ANONYMIZED";
  const heroHead1   = lang === "fr" ? "En ce moment, quelqu'un cherche " : "Right now, someone is looking for ";
  const heroHeadYou = lang === "fr" ? "vous." : "you.";
  const heroBody    = lang === "fr"
    ? "Network Assistant fait tourner des centaines de matchings en parallèle. Connectez-vous et dites-lui qui vous cherchez — quelqu'un est probablement déjà en train de vous décrire."
    : "Network Assistant runs hundreds of intent-driven matches in parallel. Sign in and tell it who you need — odds are someone is already describing you.";
  const heroPrompt  = lang === "fr" ? "décris qui tu cherches" : "describe who you need";
  const heroTermTitle = "na ~ /matchings — live";

  const looped = [...FEED, ...FEED, ...FEED];

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    const providerFromUrl = searchParams.get("provider");
    const actionFromUrl = searchParams.get("action");
    const errorFromUrl = searchParams.get("error");

    if (tokenFromUrl && providerFromUrl && actionFromUrl === "login") {
      onLogin(tokenFromUrl);
      navigate("/chat");
      return;
    }

    if (errorFromUrl) {
      const messageFromUrl = searchParams.get("message");
      setError(messageFromUrl || translations[lang].oauthError || "OAuth authorization failed");
    }
  }, [searchParams, onLogin, navigate, translations, lang]);

  const triggerPasswordSave = async () => {
    if (!("PasswordCredential" in window) || !navigator?.credentials?.store) return;
    try {
      const credential = new PasswordCredential({
        id: email.trim().toLowerCase(),
        password: password,
      });
      await navigator.credentials.store(credential);
    } catch (err) {
      // silent fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError(translations[lang]?.emailRequired || "Email is required");
      return;
    }
    if (!password.trim()) {
      setError(translations[lang]?.passwordRequired || "Password is required");
      return;
    }

    try {
      const loginData = new URLSearchParams();
      loginData.append("username", email);
      loginData.append("password", password);

      const res = await axios.post(`${API_URL}${AUTH_ENDPOINTS.LOGIN}`, loginData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const token = res.data.access_token;
      if (!token) throw new Error("Login failed");

      await triggerPasswordSave();
      onLogin(token);
      navigate("/chat");
    } catch (err) {
      let msg = translations[lang]?.error || "Error";
      if (err.response?.data?.detail) msg = err.response.data.detail;
      setError(msg);
    }
  };

  const handleSocialLogin = (provider) => {
    window.location.href = `/api/auth/${provider}/login?mode=login`;
  };

  const submitResetSendCode = async () => {
    setResetError("");
    setResetMessage("");
    if (!resetEmail.trim()) {
      setResetError(translations[lang]?.emailRequired || "Email is required");
      return;
    }
    if (!validateEmailFormat(resetEmail)) {
      setResetError(lang === "fr" ? "Format d'email invalide" : "Invalid email format");
      return;
    }
    const emailOk = await checkResetEmail();
    if (!emailOk) return;
    setResetLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/password/reset/send-code`, {
        email: resetEmail.trim().toLowerCase(),
      });
      setResetMessage(res.data?.message || "Code sent");
      setResetStep("verify");
    } catch (err) {
      setResetError(getApiErrorMessage(err, translations[lang]?.error || "Error"));
    } finally {
      setResetLoading(false);
    }
  };

  const submitResetVerifyCode = async () => {
    setResetError("");
    setResetMessage("");
    if (!resetCode.trim()) {
      setResetError("Verification code is required");
      return;
    }
    if (!/^\d{6}$/.test(resetCode.trim())) {
      setResetError(lang === "fr" ? "Le code doit contenir 6 chiffres" : "Code must be exactly 6 digits");
      return;
    }
    setResetLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/password/reset/verify-code`, {
        email: resetEmail.trim().toLowerCase(),
        code: resetCode.trim(),
      });
      setResetMessage(res.data?.message || "Code is valid");
      setResetStep("confirm");
    } catch (err) {
      setResetError(getApiErrorMessage(err, translations[lang]?.error || "Error"));
    } finally {
      setResetLoading(false);
    }
  };

  const submitResetConfirm = async () => {
    setResetError("");
    setResetMessage("");
    if (!resetNewPassword.trim()) {
      setResetError(translations[lang]?.passwordRequired || "Password is required");
      return;
    }
    if (resetNewPassword.trim().length < 6) {
      setResetError(lang === "fr" ? "Le mot de passe doit contenir au moins 6 caractères" : "Password must be at least 6 characters");
      return;
    }
    if (!/^\d{6}$/.test(resetCode.trim())) {
      setResetError(lang === "fr" ? "Le code doit contenir 6 chiffres" : "Code must be exactly 6 digits");
      return;
    }
    setResetLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/password/reset/confirm`, {
        email: resetEmail.trim().toLowerCase(),
        code: resetCode.trim(),
        new_password: resetNewPassword,
      });
      setResetMessage(res.data?.message || "Password has been reset");
      setShowResetPanel(false);
      setResetStep("send");
      setResetCode("");
      setResetNewPassword("");
    } catch (err) {
      setResetError(getApiErrorMessage(err, translations[lang]?.error || "Error"));
    } finally {
      setResetLoading(false);
    }
  };

  const textColor = isLightTheme ? "#1f2937" : themeVars.fg;
  const panelBg   = themeVars.subtleBg;

  // ── Original input/social tokens (preserved for exact mobile match) ──────
  const inputBg        = isLightTheme ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.09)";
  const inputFocusBg   = isLightTheme ? "rgba(255,255,255,0.68)" : "rgba(255,255,255,0.16)";
  const inputBorder    = isLightTheme ? "1px solid rgba(99,102,241,0.18)" : "none";
  const inputFocusShadow = isLightTheme
    ? `0 0 0 3px ${themeVars.accent}35`
    : `0 0 0 3px ${themeVars.accent}50`;
  const socialBg       = isLightTheme ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.09)";
  const socialFocusBg  = isLightTheme ? "rgba(255,255,255,0.68)" : "rgba(255,255,255,0.18)";
  const socialBorder   = isLightTheme ? "1px solid rgba(99,102,241,0.15)" : "none";
  const errorColor     = isLightTheme ? "#dc2626" : "#f87171";

  return (
    <>
      <style>{`
        ${globalAnimationStyles}
        @keyframes fadeInLanguage {
          from { opacity: 0.45; transform: translateY(18px); }
          to   { opacity: 1;    transform: translateY(0); }
        }
        @keyframes lavaDrift {
          0%, 100% { background-position: 0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%; }
          50%       { background-position: 100% 100%, 0% 0%, 0% 100%, 100% 0%, 75% 25%; }
        }
        @keyframes lavaDrift2 {
          0%, 100% { background-position: 0% 0%, 100% 100%, 50% 0%; }
          50%       { background-position: 100% 100%, 0% 0%, 100% 50%; }
        }
        @keyframes naMarqueeUp {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-33.3333%); }
        }
        @keyframes naBlink {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0; }
        }
        @keyframes gridPulse {
          0%,100%{ opacity:0.35; }
          50%    { opacity:0.55; }
        }

        /* Grid background on the LEFT panel only (not inside terminal) */
        .na-grid-bg {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background-image:
            linear-gradient(${isLightTheme ? "rgba(99,102,241,0.07)" : "rgba(255,255,255,0.04)"} 1px, transparent 1px),
            linear-gradient(90deg, ${isLightTheme ? "rgba(99,102,241,0.07)" : "rgba(255,255,255,0.04)"} 1px, transparent 1px);
          background-size: 40px 40px;
          animation: gridPulse 6s ease-in-out infinite;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
        }

        /* Two-column shell */
        .login-shell {
          min-height: calc(100dvh - var(--app-header-h, 56px));
          padding-top: var(--app-header-h, 56px);
          display: grid;
          grid-template-columns: 60% 40%;
          background: ${BG};
          box-sizing: border-box;
          overflow-x: clip;
        }
        .login-shell-left {
          position: relative;
          padding: 48px 56px 48px 64px;
          overflow: hidden;
          background: ${BG};
          display: flex;
          flex-direction: column;
          height: calc(100dvh - var(--app-header-h, 56px));
          min-height: 0;
        }
        .login-shell-right {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 32px 16px;
          box-sizing: border-box;
          min-height: calc(100dvh - var(--app-header-h, 56px));
        }
        @keyframes authDividerPulse {
          0%, 100% { height: 42%; opacity: 0.45; transform: translateY(-50%) scaleY(0.92); }
          50% { height: 68%; opacity: 0.95; transform: translateY(-50%) scaleY(1.06); }
        }
        .login-accent-line {
          position: absolute;
          left: -1px;
          top: 50%;
          transform: translateY(-50%);
          width: 2px;
          height: 52%;
          background: ${term.accentLine};
          pointer-events: none;
          animation: authDividerPulse 4.2s ease-in-out infinite;
        }

        /* Below 900px: hide terminal, form fills viewport — ORIGINAL mobile UX */
@media (max-width: 900px) {
  .login-shell {
    grid-template-columns: 1fr;
    min-height: calc(100svh - var(--app-header-h, 56px));  /* ← ИЗМЕНЕНО: 100svh для мобилы */
  }
  .login-shell-left { display: none; }
  .login-shell-right {
    border-left: none;
    padding: 32px 16px;
    min-height: calc(100svh - var(--app-header-h, 56px));  /* ← ИЗМЕНЕНО */
  }
  .login-accent-line { display: none; }
}
        @media (max-width: 600px) {
          .login-shell {
            background: ${panelBg};
          }
          .login-shell-right {
            padding: 0;
            align-items: center;
          }
        }

        .login-card {
          width: 100%;
          max-width: 440px;
          background: transparent;
          border-radius: 0;
          padding: 44px 36px 36px;
          box-sizing: border-box;
          box-shadow: none;
        }
        @media (max-width: 600px) {
          .login-card {
            background: transparent;
            border-radius: 0;
            width: 100%;
            min-height: unset;
            height: auto;
            padding: 20px 20px 32px;
            box-shadow: none;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
          }
        }
      `}</style>

      <div className="login-shell" style={{ color: textColor }}>

        {/* ==================== LEFT — terminal feed ==================== */}
        <div className="login-shell-left">
          <div className="na-grid-bg" style={{ opacity: isLightTheme ? 0.7 : 0.6 }} />

          {/* Glow orbs (matched with RegisterPage) */}
          <div style={{
            position: "absolute", width: 420, height: 420,
            borderRadius: "50%",
            background: isLightTheme
              ? "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)",
            top: -120, left: -100,
            pointerEvents: "none",
          }} />
          <div style={{
            position: "absolute", width: 280, height: 280,
            borderRadius: "50%",
            background: isLightTheme
              ? "radial-gradient(circle, rgba(34,211,238,0.1) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(34,211,238,0.12) 0%, transparent 70%)",
            bottom: 60, right: -60,
            pointerEvents: "none",
          }} />

          <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.2em",
                color: term.eyebrow,
                marginBottom: 10,
                fontFamily: MONO_STACK,
              }}>
                {heroEyebrow}
              </div>
              <h1 style={{
                fontSize: 42,
                fontWeight: 700,
                letterSpacing: "-0.025em",
                margin: 0,
                lineHeight: 1.05,
                color: term.headerText,
              }}>
                {heroHead1}
                <span style={{ position: "relative", display: "inline-block" }}>
                  <span style={{
                    background: `linear-gradient(135deg, ${term.gradFrom}, ${term.gradTo})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}>
                    {heroHeadYou}
                  </span>
                </span>
              </h1>
              <p style={{
                fontSize: 15,
                color: term.bodyText,
                marginTop: 14,
                maxWidth: 520,
                lineHeight: 1.55,
              }}>
                {heroBody}
              </p>
            </div>

            {/* Terminal — solid color, NO grid inside */}
            <div style={{
              flex: 1,
              minHeight: 0,
              borderRadius: 14,
              border: term.terminalBorder,
              background: term.terminalBg,
              boxShadow: term.terminalShadow,
              padding: "14px 18px",
              overflow: "hidden",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              backdropFilter: isLightTheme ? "blur(6px)" : "none",
              WebkitBackdropFilter: isLightTheme ? "blur(6px)" : "none",
            }}>
              {/* terminal header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                paddingBottom: 10,
                borderBottom: term.terminalHeaderBorder,
                marginBottom: 10,
              }}>
                <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#ff5f57" }} />
                <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#febc2e" }} />
                <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#28c840" }} />
                <span style={{
                  marginLeft: 12,
                  fontFamily: MONO_STACK,
                  fontSize: 11.5,
                  color: term.muted,
                  letterSpacing: "0.04em",
                }}>
                  {heroTermTitle}
                </span>
              </div>

              {/* marquee */}
              <div style={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                position: "relative",
                maskImage: "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
              }}>
                <div style={{
                  animation: "naMarqueeUp 58s linear infinite",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  fontFamily: MONO_STACK,
                  fontSize: 13,
                }}>
                  {looped.map((row, i) => {
                    const tag = row[1];
                    const tagColor =
                      tag === "matched"    ? term.tagMatched :
                      tag === "intro sent" ? term.tagIntro   :
                                             term.tagMatching;
                    return (
                      <div key={i} style={{
                        display: "grid",
                        gridTemplateColumns: "42px 92px 1fr",
                        gap: 14,
                        alignItems: "baseline",
                        padding: "6px 0",
                        borderBottom: term.rowBorder,
                      }}>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          color: row[0] === "NEW" ? term.tagNew : "transparent",
                        }}>
                          {row[0] || "·"}
                        </span>
                        <span style={{ fontSize: 11.5, color: tagColor }}>
                          [{row[1]}]
                        </span>
                        <span style={{ color: term.rowText }}>
                          {row[2]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* prompt */}
              <div style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: term.terminalHeaderBorder,
                fontFamily: MONO_STACK,
                fontSize: 13,
                color: term.promptText,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                <span style={{ color: term.prompt }}>na&nbsp;&gt;</span>
                <span>{heroPrompt}</span>
                <span style={{
                  display: "inline-block",
                  width: 8,
                  height: 14,
                  background: term.cursor,
                  animation: "naBlink 1s steps(2) infinite",
                }} />
              </div>
            </div>
          </div>
        </div>

        {/* ==================== RIGHT — login form (unchanged logic) ==================== */}
        <div className="login-shell-right">
          <div className="login-accent-line" />

          <div className="login-card">

            <h2
              className="animate-on-mount"
              style={{
                margin: isMobile ? "0 0 16px" : "0 0 28px",
                fontSize: isMobile ? "1.7rem" : "2.2rem",
                fontWeight: 700,
                textAlign: "center",
                letterSpacing: "-0.02em",
                ...(isSamsungBrowser
                  ? { color: "#3730a3", background: "none", WebkitTextFillColor: "unset" }
                  : isLightTheme
                  ? {
                      background: accentGradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      color: themeVars.accent,
                    }
                  : {
                      background: accentGradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }),
              }}
            >
              {translations[lang]?.login || "Sign in"}
            </h2>

            <form
              key={lang}
              onSubmit={handleSubmit}
              autoComplete="on"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "12px" : "18px",
                animation: "fadeInLanguage 0.48s ease-out",
                width: "100%",
              }}
            >
              <input
                className="animate-on-mount"
                type="email"
                name="email"
                autoComplete="username email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={translations[lang]?.email || "Email"}
                required
                style={{
                  background: isSamsungBrowser ? "rgba(255,255,255,0.55)" : inputBg,
                  color: textColor,
                  border: isSamsungBrowser ? "1px solid rgba(99,102,241,0.25)" : inputBorder,
                  borderRadius: "10px",
                  padding: isMobile ? "13px 18px" : "16px 20px",
                  fontSize: "1.08rem",
                  outline: "none",
                  width: "100%",
                  boxSizing: "border-box",
                  transition: "all 0.28s ease",
                  boxShadow: isLightTheme && !isSamsungBrowser ? "0 1px 3px rgba(0,0,0,0.07)" : "none",
                }}
                onFocus={(e) => {
                  e.target.style.background = isSamsungBrowser ? "rgba(255,255,255,0.75)" : inputFocusBg;
                  e.target.style.boxShadow = isSamsungBrowser ? `0 0 0 3px ${themeVars.accent}50` : inputFocusShadow;
                }}
                onBlur={(e) => {
                  e.target.style.background = isSamsungBrowser ? "rgba(255,255,255,0.55)" : inputBg;
                  e.target.style.boxShadow = isLightTheme && !isSamsungBrowser ? "0 1px 3px rgba(0,0,0,0.07)" : "none";
                }}
              />

              <input
                className="animate-on-mount"
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={translations[lang]?.password || "Password"}
                required
                style={{
                  background: isSamsungBrowser ? "rgba(255,255,255,0.55)" : inputBg,
                  color: textColor,
                  border: isSamsungBrowser ? "1px solid rgba(99,102,241,0.25)" : inputBorder,
                  borderRadius: "10px",
                  padding: isMobile ? "13px 18px" : "16px 20px",
                  fontSize: "1.08rem",
                  outline: "none",
                  width: "100%",
                  boxSizing: "border-box",
                  transition: "all 0.28s ease",
                  boxShadow: isLightTheme && !isSamsungBrowser ? "0 1px 3px rgba(0,0,0,0.07)" : "none",
                }}
                onFocus={(e) => {
                  e.target.style.background = isSamsungBrowser ? "rgba(255,255,255,0.75)" : inputFocusBg;
                  e.target.style.boxShadow = isSamsungBrowser ? `0 0 0 3px ${themeVars.accent}50` : inputFocusShadow;
                }}
                onBlur={(e) => {
                  e.target.style.background = isSamsungBrowser ? "rgba(255,255,255,0.55)" : inputBg;
                  e.target.style.boxShadow = isLightTheme && !isSamsungBrowser ? "0 1px 3px rgba(0,0,0,0.07)" : "none";
                }}
              />

              {isSamsungBrowser ? (
                <button
                  className="animate-on-mount"
                  type="submit"
                  style={{
                    width: "100%",
                    margin: "12px 0 0",
                    display: "block",
                    border: "none",
                    borderRadius: 36,
                    cursor: "pointer",
                    background: "rgba(220,222,248,0.92)",
                    color: "#3730a3",
                    padding: isMobile ? "14px" : "17px",
                    fontSize: "1.12rem",
                    fontWeight: 600,
                    transition: "background 0.25s ease, box-shadow 0.25s ease",
                    boxShadow: "0 4px 16px rgba(99,102,241,0.15)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(205,208,242,0.97)";
                    e.currentTarget.style.boxShadow = "0 6px 22px rgba(99,102,241,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(220,222,248,0.92)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,0.15)";
                  }}
                >
                  {translations[lang]?.login || "Sign in"}
                </button>
              ) : (
                <button
                  className="animate-on-mount"
                  type="submit"
                  style={{
                    width: "100%",
                    margin: "12px 0 0",
                    border: "none",
                    borderRadius: 36,
                    overflow: "hidden",
                    position: "relative",
                    cursor: "pointer",
                    background: "transparent",
                    padding: 0,
                    isolation: "isolate",
                    transition: "all 0.2s ease",
                    boxShadow: isLightTheme
                      ? "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                      : "0 25px 90px rgba(99,102,241,0.3)",
                    display: "block",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = isLightTheme
                      ? "translateY(-2px) scale(1.015)"
                      : "translateY(-12px) scale(1.022)";
                    e.currentTarget.style.boxShadow = isLightTheme
                      ? "0 15px 35px -5px rgba(0, 0, 0, 0.2), 0 10px 15px -6px rgba(0, 0, 0, 0.15)"
                      : "0 50px 140px rgba(99,102,241,0.55)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = isLightTheme
                      ? "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                      : "0 25px 90px rgba(99,102,241,0.3)";
                  }}
                >
                  <div
                    className="lava-core"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `
                        radial-gradient(circle at 30% 40%, #10b981 0%, transparent 40%),
                        radial-gradient(circle at 70% 60%, #6366f1 0%, transparent 45%),
                        radial-gradient(circle at 20% 80%, #8b5cf6 0%, transparent 38%),
                        radial-gradient(circle at 80% 30%, #ec4899 0%, transparent 42%),
                        linear-gradient(160deg, #22d3ee 0%, #c084fc 100%)
                      `,
                      backgroundSize: "180% 180%, 200% 200%, 220% 220%, 190% 190%, 300% 300%",
                      backgroundPosition: "0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%",
                      opacity: 0.75,
                      animation: "lavaDrift 38s linear infinite",
                      filter: "blur(4px) saturate(1.4)",
                      mixBlendMode: "screen",
                    }}
                  />
                  <div
                    className="lava-overlay"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `
                        radial-gradient(circle at 55% 25%, #10b98188 0%, transparent 50%),
                        radial-gradient(circle at 15% 65%, #6366f188 0%, transparent 55%),
                        radial-gradient(circle at 85% 75%, #8b5cf688 0%, transparent 48%)
                      `,
                      backgroundSize: "240% 240%, 280% 280%, 260% 260%",
                      opacity: 0.6,
                      animation: "lavaDrift2 52s linear infinite reverse",
                      filter: "blur(6px)",
                      mixBlendMode: "overlay",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 4,
                      padding: isMobile ? "14px" : "17px",
                      color: "#ffffff",
                      textShadow: isLightTheme
                        ? "0 1px 3px rgba(0,0,0,0.35)"
                        : "0 2px 12px rgba(0,0,0,0.7)",
                      fontSize: "1.12rem",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {translations[lang]?.login || "Sign in"}
                  </div>
                </button>
              )}

              <div style={{ textAlign: "center", marginTop: "4px" }}>
                <div
                  className="animate-on-mount"
                  onClick={() => {
                    setShowResetPanel((v) => !v);
                    setResetEmail((prev) => prev || email.trim().toLowerCase());
                    setResetError("");
                    setResetMessage("");
                  }}
                  style={{
                    color: themeVars.accent,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.95rem",
                    marginBottom: "8px",
                  }}
                >
                  {lang === "fr" ? "Mot de passe oublié ?" : "Forgot password?"}
                </div>
                <span
                  className="animate-on-mount"
                  onClick={() => navigate("/register")}
                  style={{
                    color: themeVars.accent,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "1rem",
                  }}
                >
                  {translations[lang]?.no_account || "Don't have an account? Register"}
                </span>
              </div>

              {showResetPanel && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "14px",
                    borderRadius: "12px",
                    background: isLightTheme ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.18)",
                    border: isLightTheme ? "1px solid rgba(99,102,241,0.2)" : "1px solid rgba(99,102,241,0.3)",
                  }}
                >
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => {
                      setResetEmail(e.target.value);
                      if (resetError) setResetError("");
                    }}
                    onBlur={() => {
                      if (!resetEmail.trim()) return;
                      if (!validateEmailFormat(resetEmail)) {
                        setResetError(lang === "fr" ? "Format d'email invalide" : "Invalid email format");
                      }
                    }}
                    placeholder={translations[lang]?.email || "Email"}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      marginBottom: "10px",
                      borderRadius: "10px",
                      padding: "10px 12px",
                      border: inputBorder,
                      background: inputBg,
                      color: textColor,
                      fontSize: "1rem",
                      outline: "none",
                    }}
                  />

                  {resetStep !== "send" && (
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={formatResetCode(resetCode)}
                      onChange={(e) => setResetCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                      placeholder="000 000"
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        marginBottom: "10px",
                        borderRadius: "10px",
                        padding: "10px 12px",
                        border: inputBorder,
                        background: inputBg,
                        color: textColor,
                        letterSpacing: "0.12em",
                        textAlign: "center",
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "1rem",
                        outline: "none",
                      }}
                    />
                  )}

                  {resetStep === "confirm" && (
                    <input
                      type="password"
                      value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e.target.value)}
                      placeholder={lang === "fr" ? "Nouveau mot de passe" : "New password"}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        marginBottom: "10px",
                        borderRadius: "10px",
                        padding: "10px 12px",
                        border: inputBorder,
                        background: inputBg,
                        color: textColor,
                        fontSize: "1rem",
                        outline: "none",
                      }}
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      if (resetStep === "send") return submitResetSendCode();
                      if (resetStep === "verify") return submitResetVerifyCode();
                      return submitResetConfirm();
                    }}
                    disabled={resetLoading}
                    style={{
                      width: "100%",
                      border: "none",
                      borderRadius: "10px",
                      padding: "11px 12px",
                      background: themeVars.btnBg,
                      color: "#fff",
                      cursor: resetLoading ? "not-allowed" : "pointer",
                      opacity: resetLoading ? 0.7 : 1,
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    {resetLoading
                      ? (lang === "fr" ? "Envoi..." : "Submitting...")
                      : resetStep === "send"
                      ? (lang === "fr" ? "Envoyer le code" : "Send code")
                      : resetStep === "verify"
                      ? (lang === "fr" ? "Vérifier le code" : "Verify code")
                      : (lang === "fr" ? "Réinitialiser le mot de passe" : "Reset password")}
                  </button>

                  {resetMessage && (
                    <div style={{ marginTop: 8, fontSize: "0.88rem", color: isLightTheme ? "#166534" : "#86efac", textAlign: "center" }}>
                      {resetMessage}
                    </div>
                  )}
                  {resetError && (
                    <div style={{ marginTop: 8, fontSize: "0.88rem", color: errorColor, textAlign: "center" }}>
                      {resetError}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div style={{ color: errorColor, textAlign: "center", marginTop: "10px", fontSize: "0.96rem", fontWeight: 500 }}>
                  {error}
                </div>
              )}

              <div
                className="animate-on-mount"
                style={{
                  margin: isMobile ? "8px 0 4px" : "0",
                  textAlign: "center",
                  fontSize: "0.96rem",
                  color: isLightTheme ? "#4b5563" : "inherit",
                  opacity: isLightTheme ? 0.85 : 0.78,
                }}
              >
                {translations[lang]?.or_sign_in_with || "or sign in with"}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="animate-on-mount"
                  type="button"
                  onClick={() => handleSocialLogin("google")}
                  style={{
                    background: socialBg,
                    border: socialBorder,
                    borderRadius: "12px",
                    padding: isMobile ? "10px 16px" : "14px 20px",
                    fontSize: isMobile ? "0.96rem" : "1.02rem",
                    color: textColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.22s ease",
                    flex: "1 1 auto",
                    minWidth: "0",
                    maxWidth: "48%",
                    whiteSpace: "nowrap",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = socialFocusBg;
                      e.currentTarget.style.transform = "scale(1.03)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = socialBg;
                      e.currentTarget.style.transform = "scale(1)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
                    }
                  }}
                >
                  <img src="https://www.google.com/favicon.ico" alt="Google" width={18} height={18} />
                  Google
                </button>

                <button
                  className="animate-on-mount"
                  type="button"
                  onClick={() => handleSocialLogin("linkedin")}
                  style={{
                    background: socialBg,
                    border: socialBorder,
                    borderRadius: "12px",
                    padding: isMobile ? "10px 16px" : "14px 20px",
                    fontSize: isMobile ? "0.96rem" : "1.02rem",
                    color: textColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.22s ease",
                    flex: "1 1 auto",
                    minWidth: "0",
                    maxWidth: "48%",
                    whiteSpace: "nowrap",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = socialFocusBg;
                      e.currentTarget.style.transform = "scale(1.03)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = socialBg;
                      e.currentTarget.style.transform = "scale(1)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
                    }
                  }}
                >
                  <img
                    src="https://static.licdn.com/sc/h/al2o9zrvru7aqj8e1x2rzsrca"
                    alt="LinkedIn"
                    width={18}
                    height={18}
                  />
                  LinkedIn
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    </>
  );
}
